import { ConnectionHandler, graphql, useMutation } from "react-relay";
import { EventCompetitionsFragment$key } from "./__generated__/EventCompetitionsFragment.graphql";
import { EventCompetitionsDeleteMutation as EventCompetitionsDeleteMutationType } from "./__generated__/EventCompetitionsDeleteMutation.graphql";
import { EventCompetitionsUpdateMutation as EventCompetitionsUpdateMutationType } from "./__generated__/EventCompetitionsUpdateMutation.graphql";
import { usePaginationFragment } from "react-relay";
import { FormattedMessage, useIntl } from "react-intl";
import Dropdown from "./Dropdown";
import { useAuth } from "../utils/auth";
import LoadMore from "./LoadMore";
import { logger } from "../common/logger";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/kit/ui/table";
import { Button } from "@/kit/ui/button";
import { Switch } from "@/kit/ui/switch";
import { toast } from "@/utils/toast";
import { relayErrorMessage } from "@/utils/relay";
import { DotsGridIcon } from "@/icons";
import { generatePath, Link, useNavigate } from "react-router";
import { EventCompetitionCreateEventFromCompetitionMutation as EventCompetitionCreateEventFromCompetitionMutationType } from "./__generated__/EventCompetitionCreateEventFromCompetitionMutation.graphql";

const EventCompetitionsFragment = graphql`
  fragment EventCompetitionsFragment on Event
  @refetchable(queryName: "EventCompetitionsFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    id
    slug
    isPrivate
    viewerCanCreateEventFromCompetition: viewerCan(action: CREATE_EVENT)
    viewerCanUpdate: viewerCan(action: UPDATE_EVENT)
    competitions(first: $count, after: $cursor)
      @connection(key: "EventCompetitionsFragment_competitions") {
      edges {
        node {
          id
          viewerCanDelete: viewerCan(action: REMOVE_EVENT_COMPETITION)
          membersCanReadEvent
          competition {
            id
            slug
            title
            isPrivate
            ...CompetitionCardFragment
          }
        }
      }
    }
  }
`;

const EventCompetitionsDeleteMutation = graphql`
  mutation EventCompetitionsDeleteMutation($id: ID!, $connections: [ID!]!) {
    removeEventCompetition(id: $id) @deleteEdge(connections: $connections)
  }
`;

const EventCompetitionsUpdateMutation = graphql`
  mutation EventCompetitionsUpdateMutation(
    $eventId: ID!
    $competitionId: ID!
    $membersCanReadEvent: Boolean!
  ) {
    updateEventCompetition(
      eventId: $eventId
      competitionId: $competitionId
      membersCanReadEvent: $membersCanReadEvent
    ) {
      node {
        id
        membersCanReadEvent
      }
    }
  }
`;

const EventCompetitionCreateEventFromCompetitionMutation = graphql`
  mutation EventCompetitionCreateEventFromCompetitionMutation(
    $competitionId: ID!
  ) {
    createEventFromCompetition(competitionId: $competitionId) {
      node {
        id
        slug
      }
    }
  }
`;

interface Props {
  event: EventCompetitionsFragment$key;
}

export default function EventCompetitions({ event: eventFragment }: Props) {
  const { userId: viewerId } = useAuth();
  const intl = useIntl();

  const navigate = useNavigate();
  const {
    data: event,
    refetch,
    loadNext,
    hasNext,
    isLoadingNext,
  } = usePaginationFragment(EventCompetitionsFragment, eventFragment);
  const [commitDeleteMutation, isDeleteMutationInFlight] =
    useMutation<EventCompetitionsDeleteMutationType>(
      EventCompetitionsDeleteMutation,
    );
  const [commitUpdateMutation, isUpdateMutationInFlight] =
    useMutation<EventCompetitionsUpdateMutationType>(
      EventCompetitionsUpdateMutation,
    );
  const [
    commitCreateEventFromCompetitionMutation,
    isCreateEventFromCompetitionMutationInFlight,
  ] = useMutation<EventCompetitionCreateEventFromCompetitionMutationType>(
    EventCompetitionCreateEventFromCompetitionMutation,
  );

  if (event.competitions.edges.length == 0) {
    return (
      <p className="text-grey-400">
        <FormattedMessage defaultMessage="No tracks" />
      </p>
    );
  }
  const onDelete = (id: string, userId: string) => {
    commitDeleteMutation({
      variables: {
        id,
        connections: [
          ConnectionHandler.getConnectionID(
            event.id,
            "EventCompetitionsFragment_competitions",
          ),
          ConnectionHandler.getConnectionID(
            event.id,
            "EventCompetitionsCarouselFragment_competitions",
          ),
        ],
      },
      onError: (error) => {
        logger.error(error);
      },
      onCompleted: () => {
        if (userId === viewerId) {
          refetch({});
        }
      },
    });
  };

  const onToggleMembersCanReadEvent = (
    id: string,
    competitionId: string,
    membersCanReadEvent: boolean,
  ) =>
    commitUpdateMutation({
      variables: { eventId: event.id, competitionId, membersCanReadEvent },
      optimisticResponse: {
        updateEventCompetition: { node: { id, membersCanReadEvent } },
      },
      onError: (error) => {
        toast.error(relayErrorMessage(error));
      },
    });

  // the members of a track only matter to a private challenge
  const showMembersCanReadEvent = event.viewerCanUpdate && event.isPrivate;

  const onDerive = (id: string) =>
    commitCreateEventFromCompetitionMutation({
      variables: {
        competitionId: id,
      },
      onError: (error) => {
        logger.error(error);
      },
      onCompleted: ({
        createEventFromCompetition: {
          node: { slug },
        },
      }) => {
        navigate(generatePath("/challenges/:slug", { slug }));
      },
    });

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <FormattedMessage defaultMessage="Track" />
            </TableHead>
            {showMembersCanReadEvent && (
              <TableHead>
                <FormattedMessage defaultMessage="Members can view challenge" />
              </TableHead>
            )}
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {event.competitions.edges.map(
            ({
              node: { id, viewerCanDelete, membersCanReadEvent, competition },
            }) => (
              <TableRow key={id}>
                <TableCell className="whitespace-nowrap">
                  <Link
                    to={generatePath("/challenges/:owner/tracks/:slug", {
                      owner: event.slug,
                      slug: competition.slug,
                    })}
                  >
                    <div className="flex flex-row items-center hover:cursor-pointer">
                      <span>
                        <strong>{competition.slug}</strong>: {competition.title}
                      </span>
                    </div>
                  </Link>
                </TableCell>
                {showMembersCanReadEvent && (
                  <TableCell>
                    <Switch
                      aria-label={intl.formatMessage(
                        {
                          defaultMessage:
                            "Members of {track} can view the challenge",
                        },
                        { track: competition.slug },
                      )}
                      title={
                        competition.isPrivate
                          ? undefined
                          : intl.formatMessage({
                              defaultMessage:
                                "Only the members of a private track can view the challenge",
                            })
                      }
                      value={membersCanReadEvent}
                      disabled={
                        isUpdateMutationInFlight || !competition.isPrivate
                      }
                      onChange={(value) =>
                        onToggleMembersCanReadEvent(id, competition.id, value)
                      }
                    />
                  </TableCell>
                )}
                <TableCell className="whitespace-nowrap text-end">
                  {viewerCanDelete && (
                    <Dropdown
                      autoCloses
                      trigger={
                        <Button
                          size="sm"
                          icon
                          variant="secondary"
                          hue="grey"
                          className="rounded-full"
                        >
                          <DotsGridIcon />
                        </Button>
                      }
                    >
                      <Dropdown.Item
                        disabled={isDeleteMutationInFlight}
                        onClick={() => {
                          onDelete(id, competition.id);
                        }}
                      >
                        <FormattedMessage defaultMessage="Remove track" />
                      </Dropdown.Item>
                      {event.viewerCanCreateEventFromCompetition && (
                        <Dropdown.Item
                          disabled={
                            isCreateEventFromCompetitionMutationInFlight
                          }
                          onClick={() => onDerive(competition.id)}
                        >
                          <FormattedMessage defaultMessage="Derive a challenge" />
                        </Dropdown.Item>
                      )}
                    </Dropdown>
                  )}
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
      <div className="py-8">
        <LoadMore
          loadMore={loadNext}
          hasMore={hasNext}
          isLoading={isLoadingNext}
        />
      </div>
    </div>
  );
}
